import { getCountryFlag } from "@/utils/countryFlags";
import { ChevronRight } from "lucide-react";

/**
 * Compact horizontal strip showing the country sequence of a cruise.
 *
 * Renders as `🇺🇸 → 🇧🇸 → 🇲🇽 → 🇺🇸` (consecutive duplicates collapsed) so a
 * multi-country cruise's geography is visible at a glance on the cruise
 * block header — no need to expand to see what countries are in scope.
 *
 * Falls back gracefully:
 *   - If a country has no flag in the lookup, shows the country name text.
 *   - If route is empty (all sea days / no countries), renders nothing.
 *
 * Hover title reveals the full names list ("USA → Bahamas → Mexico → USA").
 */
export const CruiseCountryStrip = ({ route, className = "" }) => {
  if (!Array.isArray(route) || route.length === 0) return null;
  const titleText = route.join(" → ");
  return (
    <div
      className={`flex items-center gap-1 flex-wrap ${className}`}
      title={titleText}
      aria-label={`Cruise route: ${titleText}`}
      data-testid="cruise-country-strip"
    >
      {route.map((country, idx) => {
        const flag = getCountryFlag(country);
        return (
          <span key={`${country}-${idx}`} className="inline-flex items-center gap-1">
            {idx > 0 && (
              <ChevronRight
                className="h-3 w-3 text-cyan-700/60 dark:text-cyan-300/60"
                aria-hidden="true"
              />
            )}
            <span className="text-base leading-none" aria-hidden={!!flag}>
              {flag || country}
            </span>
          </span>
        );
      })}
    </div>
  );
};

export default CruiseCountryStrip;
